import { Location } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth, { isOAuthError } from '@okta/okta-auth-js';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { AccessDenied } from '../../../features/access-denied/access-denied';

/** OAuth error code returned by Okta when the user is not assigned to the application. */
export const ACCESS_DENIED_ERROR_CODE = 'access_denied';

/**
 * Handles the Okta sign-in redirect. It replaces the stock `OktaCallbackComponent`, which prints
 * the raw OAuth error on screen. A denied sign-in shows the access denied page on the callback URL
 * itself; any other failure shows a generic message. Error details are never rendered, and the
 * error parameters Okta appended to the URL are removed from it.
 */
@Component({
    selector: 'app-login-callback',
    imports: [TranslateModule, HlmButtonImports, RouterLink, AccessDenied],
    templateUrl: './login-callback.html',
})
export class LoginCallback implements OnInit {
    private readonly oktaAuth = inject<OktaAuth>(OKTA_AUTH);
    private readonly location = inject(Location);

    protected readonly accessDenied = signal(false);
    protected readonly failed = signal(false);

    async ngOnInit(): Promise<void> {
        try {
            // Stores the tokens and redirects back to the originally requested URL.
            await this.oktaAuth.handleLoginRedirect();
        } catch (error) {
            this.removeErrorFromUrl();
            if (isOAuthError(error) && error.errorCode === ACCESS_DENIED_ERROR_CODE) {
                this.accessDenied.set(true);
            } else {
                this.failed.set(true);
            }
        }
    }

    private removeErrorFromUrl(): void {
        this.location.replaceState(this.location.path().split('?')[0]);
    }
}